import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useArticles, useArticleCategories, useArticleFamilies, useArticleBrands } from '@/hooks/use-articles';
import { useAllStock } from '@/hooks/use-stock';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { ArticleFormDialog } from '@/pages/articles/ArticleFormDialog';
import type { Article } from '@erp/data-access/interfaces';
import {
  Badge, Button, Checkbox,
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  Input,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@erp/ui';
import { Plus, Minus, Search, PackagePlus, ShoppingCart, ScanBarcode, Eye } from '@/components/icons';
import { BarcodeScannerDialog } from '@/components/barcode-scanner-dialog';
import {
  useColumnConfig,
  ColumnConfigDialog,
  ColumnConfigButton,
  type ColDef,
} from '@/components/ui/column-config';
import { SearchableSelect } from '@/components/ui/searchable-select';

function formatPrice(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

const PICKER_COL_DEFS: ColDef[] = [
  { key: 'code',        labelKey: 'articles.code',       locked: true },
  { key: 'name',        labelKey: 'articles.name',       locked: true },
  { key: 'category',    labelKey: 'articles.category' },
  { key: 'stock',       labelKey: 'stock.currentStock' },
  { key: 'salePriceHt', labelKey: 'articles.salePriceHt' },
  { key: 'vatRate',     labelKey: 'articles.vatRate' },
  { key: 'barcode',     labelKey: 'articles.barcode' },
  { key: 'family',      labelKey: 'articles.family' },
  { key: 'brand',       labelKey: 'articles.brand' },
  { key: 'unit',        labelKey: 'articles.unit' },
];
const PICKER_DEFAULT_COLS = ['code', 'name', 'category', 'stock', 'salePriceHt', 'vatRate'];
const PICKER_RESULT_LIMIT = 120;

export interface ArticleWithQty {
  article: Article;
  quantity: number;
}

interface ArticlePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (article: Article) => void;
  onSelectMultiple?: (items: ArticleWithQty[]) => void;
}

export function ArticlePickerDialog({ open, onOpenChange, onSelect, onSelectMultiple }: ArticlePickerDialogProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [familyFilter, setFamilyFilter] = useState<string>('');
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scanFeedback, setScanFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [cart, setCart] = useState<Map<string, number>>(new Map());
  const [selectedArticlesById, setSelectedArticlesById] = useState<Map<string, Article>>(new Map());
  const [viewingArticle, setViewingArticle] = useState<Article | null>(null);
  const [colDialogOpen, setColDialogOpen] = useState(false);

  const colCfg = useColumnConfig({
    storageKey: 'erp_article_picker_columns',
    allColumns: PICKER_COL_DEFS,
    defaultColumns: PICKER_DEFAULT_COLS,
  });

  const { data: categories } = useArticleCategories();
  const { data: families } = useArticleFamilies();
  const { data: brands } = useArticleBrands();
  const { data: allStock } = useAllStock();
  const stockMap = useMemo(() => {
    const map = new Map<string, number>();
    allStock?.forEach((s) => map.set(s.articleId, (map.get(s.articleId) ?? 0) + s.quantity));
    return map;
  }, [allStock]);
  const familyMap = useMemo(() => {
    const map = new Map<string, string>();
    families?.forEach((f) => map.set(f.id, f.name));
    return map;
  }, [families]);
  const brandMap = useMemo(() => {
    const map = new Map<string, string>();
    brands?.forEach((b) => map.set(b.id, b.name));
    return map;
  }, [brands]);
  const debouncedSearch = useDebouncedValue(search.trim(), 180);
  const { data: allArticles, isLoading } = useArticles({
    search: debouncedSearch || undefined,
    categoryId: categoryFilter || undefined,
    family: familyFilter || undefined,
    brand: brandFilter || undefined,
    isActive: true,
    limit: PICKER_RESULT_LIMIT,
  });

  const familyOptions = useMemo(() => {
    if (!families) return [];
    return [...families].sort((a, b) => a.name.localeCompare(b.name));
  }, [families]);
  const brandOptions = useMemo(() => {
    if (!brands) return [];
    return [...brands].sort((a, b) => a.name.localeCompare(b.name));
  }, [brands]);

  const articles = allArticles;

  const resetState = () => {
    setSearch('');
    setCategoryFilter('');
    setFamilyFilter('');
    setBrandFilter('');
    setCart(new Map());
    setSelectedArticlesById(new Map());
    setShowScanner(false);
    setScanFeedback(null);
  };

  const handleScanResult = (code: string) => {
    const normalized = code.trim();
    if (!normalized) return;
    // Try to match a currently-loaded article by barcode or code (case-insensitive).
    const match = articles?.find(
      (a) =>
        a.barcode?.toLowerCase() === normalized.toLowerCase() ||
        a.code?.toLowerCase() === normalized.toLowerCase(),
    );
    if (match) {
      addToCart(match);
      setScanFeedback({ type: 'success', text: `${match.name} (+1)` });
    } else {
      // Fall back to filtering the list by the scanned value so the user can
      // confirm and tap manually (or see the empty-state with a "create" CTA).
      setSearch(normalized);
      setScanFeedback({ type: 'error', text: `${t('sales.picker.scanNotFound')}: ${normalized}` });
    }
    setShowScanner(false);
    // Auto-clear feedback after 2.5s
    window.setTimeout(() => setScanFeedback(null), 2500);
  };

  const addToCart = (article: Article) => {
    setCart((prev) => {
      const next = new Map(prev);
      next.set(article.id, (next.get(article.id) ?? 0) + 1);
      return next;
    });
    setSelectedArticlesById((prev) => {
      const next = new Map(prev);
      next.set(article.id, article);
      return next;
    });
  };

  const removeFromCart = (articleId: string) => {
    setCart((prev) => {
      const next = new Map(prev);
      const current = next.get(articleId) ?? 0;
      if (current <= 1) next.delete(articleId);
      else next.set(articleId, current - 1);
      return next;
    });
  };

  const setCartQty = (articleId: string, qty: number) => {
    setCart((prev) => {
      const next = new Map(prev);
      if (qty > 0) next.set(articleId, qty);
      else next.delete(articleId);
      return next;
    });
  };

  const handleRowClick = (article: Article) => {
    addToCart(article);
  };

  const handleAddSelection = () => {
    const items: ArticleWithQty[] = [];
    cart.forEach((qty, articleId) => {
      const article = selectedArticlesById.get(articleId);
      if (article && qty > 0) items.push({ article, quantity: qty });
    });
    if (items.length > 0 && onSelectMultiple) {
      onSelectMultiple(items);
    } else if (items.length === 1) {
      // Fallback for single item if onSelectMultiple not provided
      onSelect(items[0]!.article);
    }
    onOpenChange(false);
    resetState();
  };

  const cartCount = cart.size;
  const cartTotalItems = Array.from(cart.values()).reduce((s, q) => s + q, 0);

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetState(); }}>
        <DialogContent className="flex flex-col gap-4 p-4 sm:p-6 max-md:inset-0 max-md:top-0 max-md:start-0 max-md:translate-x-0 max-md:rtl:translate-x-0 max-md:translate-y-0 max-md:max-w-full max-md:max-h-none max-md:w-screen max-md:h-[100dvh] max-md:rounded-none md:max-w-6xl md:max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-base md:text-xl">{t('sales.selectArticle')}</DialogTitle>
            <DialogDescription className="max-md:hidden">{t('sales.picker.articlePickerHint')}</DialogDescription>
          </DialogHeader>

          {/* Filters — single line at every viewport */}
          <div className="flex gap-2 items-center">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
              <Input
                placeholder={t('sales.picker.searchArticle')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-8 h-9 text-sm"
                autoFocus
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => setShowScanner(true)}
              title={t('sales.picker.scanBarcode')}
              aria-label={t('sales.picker.scanBarcode')}
            >
              <ScanBarcode size={16} />
            </Button>
            <div className="h-9 w-auto max-w-[140px] md:w-44 md:max-w-none shrink min-w-0" title={t('articles.category')}>
              <SearchableSelect
                options={(categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
                value={categoryFilter}
                onChange={setCategoryFilter}
                label={t('articles.category')}
                placeholder={t('common.all')}
                searchPlaceholder={t('common.search')}
                className="h-9"
              />
            </div>
            {familyOptions.length > 0 && (
              <div className="hidden md:block h-9 md:w-44 shrink min-w-0" title={t('articles.family')}>
                <SearchableSelect
                  options={familyOptions.map((f) => ({ value: f.id, label: f.name }))}
                  value={familyFilter}
                  onChange={setFamilyFilter}
                  label={t('articles.family')}
                  placeholder={t('common.all')}
                  searchPlaceholder={t('common.search')}
                  className="h-9"
                />
              </div>
            )}
            {brandOptions.length > 0 && (
              <div className="hidden md:block h-9 md:w-44 shrink min-w-0" title={t('articles.brand')}>
                <SearchableSelect
                  options={brandOptions.map((b) => ({ value: b.id, label: b.name }))}
                  value={brandFilter}
                  onChange={setBrandFilter}
                  label={t('articles.brand')}
                  placeholder={t('common.all')}
                  searchPlaceholder={t('common.search')}
                  className="h-9"
                />
              </div>
            )}
            <div className="hidden md:block shrink-0">
              <ColumnConfigButton onClick={() => setColDialogOpen(true)} />
            </div>
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-9 px-2 md:px-3 shrink-0"
              onClick={() => setShowCreateDialog(true)}
              title={t('sales.picker.newArticle')}
              aria-label={t('sales.picker.newArticle')}
            >
              <PackagePlus size={14} className="md:me-1.5" />
              <span className="hidden md:inline">{t('sales.picker.newArticle')}</span>
            </Button>
          </div>

          {/* Scan feedback toast (inline) */}
          {scanFeedback && (
            <div
              className={`rounded-md border px-3 py-2 text-xs font-medium ${
                scanFeedback.type === 'success'
                  ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-200'
                  : 'border-destructive/30 bg-destructive/10 text-destructive'
              }`}
              role="status"
            >
              {scanFeedback.text}
            </div>
          )}

          {/* Results count + cart summary */}
          <div className="flex items-center justify-between -mt-1">
            <p className="text-xs text-muted-foreground">
              {!isLoading && articles ? `${articles.length} ${t('sales.picker.resultsFound')}` : ''}
            </p>
            {cartCount > 0 && (
              <p className="text-xs font-medium text-primary">
                <ShoppingCart size={12} className="inline mr-1" />
                {cartCount} {t('sales.picker.selected')} — {cartTotalItems} {t('sales.picker.totalItems')}
              </p>
            )}
          </div>

          {/* Mobile card list */}
          <div className="md:hidden flex-1 overflow-y-auto -mx-1 px-1 space-y-2 min-h-[200px]">
            {isLoading ? (
              <p className="text-center py-12 text-muted-foreground text-sm">{t('common.loading')}</p>
            ) : !articles?.length ? (
              <div className="flex flex-col items-center gap-3 py-12">
                <Search size={32} className="text-muted-foreground/40" />
                <p className="text-muted-foreground text-sm">{t('common.noResults')}</p>
                <Button variant="outline" size="sm" onClick={() => setShowCreateDialog(true)}>
                  <Plus size={14} className="me-1.5" />
                  {t('sales.picker.newArticle')}
                </Button>
              </div>
            ) : (
              articles.map((article) => {
                const cat = categories?.find((c) => c.id === article.categoryId);
                const stockQty = stockMap.get(article.id) ?? 0;
                const qty = cart.get(article.id) ?? 0;
                return (
                  <div
                    key={article.id}
                    role="button"
                    tabIndex={0}
                    className={`border rounded-lg p-3 transition-colors cursor-pointer ${qty > 0 ? 'border-primary bg-primary/5' : 'bg-card'}`}
                    onClick={() => handleRowClick(article)}
                  >
                    <div className="flex items-start gap-3">
                      <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
                        <Checkbox
                          checked={qty > 0}
                          onCheckedChange={(checked) => {
                            if (checked) addToCart(article);
                            else setCartQty(article.id, 0);
                          }}
                          aria-label={t('common.select')}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground font-mono">{article.code}</span>
                        </div>
                        <p className="font-medium text-sm mt-0.5 break-words">{article.name}</p>
                        <div className="flex items-center gap-2 mt-1.5 text-xs flex-wrap">
                          {cat && (
                            <Badge variant="outline" className="text-muted-foreground">
                              {cat.name}
                            </Badge>
                          )}
                          <span className={stockQty <= 0 ? 'text-destructive' : stockQty <= article.stockMin ? 'text-yellow-600' : 'text-green-600'}>
                            {t('stock.currentStock')}: {stockQty}
                          </span>
                        </div>
                      </div>
                      <div className="text-end shrink-0">
                        <p className="font-semibold text-sm whitespace-nowrap">{formatPrice(article.salePriceHt)} DT</p>
                        <p className="text-xs text-muted-foreground">TVA {article.vatRate}%</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className="text-muted-foreground hover:text-primary me-auto"
                        onClick={() => setViewingArticle(article)}
                        aria-label={t('common.view')}
                      >
                        <Eye size={14} />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        disabled={qty === 0}
                        onClick={() => removeFromCart(article.id)}
                      >
                        <Minus size={14} />
                      </Button>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={qty || ''}
                        onChange={(e) => setCartQty(article.id, Number(e.target.value) || 0)}
                        className="h-8 w-14 text-center px-1"
                        placeholder="0"
                        inputMode="numeric"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        onClick={() => addToCart(article)}
                      >
                        <Plus size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Desktop results table */}
          <div className="hidden md:block flex-1 overflow-y-auto border rounded-lg min-h-[250px]">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="py-2 w-8" />
                  {colCfg.visibleCols.map((key) => {
                    const col = PICKER_COL_DEFS.find((c) => c.key === key);
                    if (!col) return null;
                    const align = (key === 'stock' || key === 'salePriceHt' || key === 'vatRate') ? 'text-end' : '';
                    return (
                      <TableHead key={key} className={`py-2 text-sm font-semibold ${align}`}>
                        {t(col.labelKey)}
                      </TableHead>
                    );
                  })}
                  <TableHead className="py-2 text-sm font-semibold text-center w-32">{t('sales.quantity')}</TableHead>
                  <TableHead className="py-2 w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={colCfg.visibleCols.length + 3} className="text-center py-12 text-muted-foreground text-sm">{t('common.loading')}</TableCell>
                  </TableRow>
                ) : !articles?.length ? (
                  <TableRow>
                    <TableCell colSpan={colCfg.visibleCols.length + 3} className="text-center py-12">
                      <div className="flex flex-col items-center gap-3">
                        <Search size={32} className="text-muted-foreground/40" />
                        <p className="text-muted-foreground">{t('common.noResults')}</p>
                        <Button variant="outline" size="sm" onClick={() => setShowCreateDialog(true)}>
                          <Plus size={14} className="me-1.5" />
                          {t('sales.picker.newArticle')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  articles.map((article) => {
                    const cat = categories?.find((c) => c.id === article.categoryId);
                    const stockQty = stockMap.get(article.id) ?? 0;
                    const qty = cart.get(article.id) ?? 0;

                    return (
                      <TableRow
                        key={article.id}
                        className={`cursor-pointer ${qty > 0 ? 'bg-primary/5' : ''} hover:bg-accent transition-colors`}
                        onClick={() => handleRowClick(article)}
                      >
                        <TableCell className="py-2 w-8" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={qty > 0}
                            onCheckedChange={(checked) => {
                              if (checked) addToCart(article);
                              else setCartQty(article.id, 0);
                            }}
                            aria-label={t('common.select')}
                          />
                        </TableCell>
                        {colCfg.visibleCols.map((key) => {
                          switch (key) {
                            case 'code':
                              return <TableCell key={key} className="text-sm py-2">{article.code}</TableCell>;
                            case 'name':
                              return <TableCell key={key} className="font-medium text-sm py-2">{article.name}</TableCell>;
                            case 'category':
                              return (
                                <TableCell key={key} className="py-2">
                                  {cat ? <Badge variant="outline" className="text-muted-foreground">{cat.name}</Badge> : <span className="text-muted-foreground">—</span>}
                                </TableCell>
                              );
                            case 'stock':
                              return (
                                <TableCell key={key} className="text-end text-sm py-2">
                                  <span className={stockQty <= 0 ? 'text-destructive' : stockQty <= article.stockMin ? 'text-yellow-600' : 'text-green-600'}>{stockQty}</span>
                                </TableCell>
                              );
                            case 'salePriceHt':
                              return <TableCell key={key} className="text-end text-sm py-2">{formatPrice(article.salePriceHt)} DT</TableCell>;
                            case 'vatRate':
                              return <TableCell key={key} className="text-end text-sm py-2">{article.vatRate}%</TableCell>;
                            case 'barcode':
                              return <TableCell key={key} className="text-sm py-2 font-mono">{article.barcode || <span className="text-muted-foreground">—</span>}</TableCell>;
                            case 'family': {
                              const familyName = article.family ? familyMap.get(article.family) : null;
                              return <TableCell key={key} className="text-sm py-2">{familyName || <span className="text-muted-foreground">—</span>}</TableCell>;
                            }
                            case 'brand': {
                              const brandName = article.brand ? brandMap.get(article.brand) : null;
                              return <TableCell key={key} className="text-sm py-2">{brandName || <span className="text-muted-foreground">—</span>}</TableCell>;
                            }
                            case 'unit':
                              return <TableCell key={key} className="text-sm py-2">{article.unit || <span className="text-muted-foreground">—</span>}</TableCell>;
                            default:
                              return null;
                          }
                        })}
                        <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon-xs"
                              className="h-6 w-6"
                              disabled={qty === 0}
                              onClick={() => removeFromCart(article.id)}
                            >
                              <Minus size={12} />
                            </Button>
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              value={qty || ''}
                              onChange={(e) => setCartQty(article.id, Number(e.target.value) || 0)}
                              className="h-6 w-12 text-center text-sm px-1"
                              placeholder="0"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon-xs"
                              className="h-6 w-6"
                              onClick={() => addToCart(article)}
                            >
                              <Plus size={12} />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="py-2 w-10" onClick={(e) => e.stopPropagation()}>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-xs"
                            className="h-6 w-6 text-muted-foreground hover:text-primary"
                            onClick={() => setViewingArticle(article)}
                            aria-label={t('common.view')}
                          >
                            <Eye size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Footer — always visible */}
          <DialogFooter>
            <Button variant="outline" onClick={() => { onOpenChange(false); resetState(); }}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleAddSelection} disabled={cartCount === 0}>
              <ShoppingCart size={14} className="me-1.5" />
              {t('sales.picker.addSelection')} ({cartTotalItems})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ArticleFormDialog
        open={showCreateDialog}
        onOpenChange={() => setShowCreateDialog(false)}
      />

      <ArticleFormDialog
        open={viewingArticle !== null}
        onOpenChange={(o) => { if (!o) setViewingArticle(null); }}
        article={viewingArticle}
      />

      <BarcodeScannerDialog
        open={showScanner}
        onOpenChange={setShowScanner}
        onScanned={handleScanResult}
      />

      <ColumnConfigDialog
        open={colDialogOpen}
        onOpenChange={setColDialogOpen}
        visibleCols={colCfg.visibleCols}
        allColumns={colCfg.allColumns}
        toggleCol={colCfg.toggleCol}
        moveCol={colCfg.moveCol}
        resetCols={colCfg.resetCols}
      />
    </>
  );
}
